import React, { useState } from 'react';
import { Moon, AlertTriangle, Info } from 'lucide-react';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Outil INSPIRÉ du cadre de stratification du risque IDF-DAR (International
// Diabetes Federation & Diabetes and Ramadan International Alliance), reconstruit
// à partir des facteurs et pondérations publiquement documentés (type de diabète,
// ancienneté, hypoglycémies, HbA1c, traitement). Ce n'est PAS une reproduction
// certifiée de l'outil officiel : les seuils exacts de l'algorithme complet
// n'étant pas publiés dans le détail, ce score est une approximation indicative.
// Les situations à très haut risque (grossesse, dialyse, acidocétose récente...)
// sont traitées séparément, en dehors du score, par prudence.

const AUTO_VERY_HIGH = [
  { key: 'grossesse', labelKey: 'ramadan_auto_1' },
  { key: 'dialyse', labelKey: 'ramadan_auto_2' },
  { key: 'acido', labelKey: 'ramadan_auto_3' },
  { key: 'hypo_non_percue', labelKey: 'ramadan_auto_4' },
  { key: 'aigu', labelKey: 'ramadan_auto_5' },
  { key: 'cardio_recent', labelKey: 'ramadan_auto_6' },
  { key: 'cognitif', labelKey: 'ramadan_auto_7' },
];

const TREATMENTS = [
  { key: 'autre', labelKey: 'ramadan_treat_1', points: 0 },
  { key: 'gliclazide', labelKey: 'ramadan_treat_2', points: 0.5 },
  { key: 'glibenclamide', labelKey: 'ramadan_treat_3', points: 1 },
  { key: 'insuline_basale', labelKey: 'ramadan_treat_4', points: 1.5 },
  { key: 'insuline_mixte_1x', labelKey: 'ramadan_treat_5', points: 2 },
  { key: 'basal_bolus', labelKey: 'ramadan_treat_6', points: 2.5 },
  { key: 'insuline_multiple', labelKey: 'ramadan_treat_7', points: 3 },
];

const RamadanDiabetesRisk = () => {
  const { t } = useLanguage();
  const [autoFlags, setAutoFlags] = useState({});
  const [type1, setType1] = useState(false);
  const [longStanding, setLongStanding] = useState(false);
  const [hypoFreq, setHypoFreq] = useState('aucune');
  const [hba1c, setHba1c] = useState('lt75');
  const [treatment, setTreatment] = useState('autre');
  const [showResult, setShowResult] = useState(false);

  const anyAutoFlag = Object.values(autoFlags).some(Boolean);

  const hypoPoints = { aucune: 0, rare: 1, frequente: 3.5, severe: 5.5 }[hypoFreq];
  const hba1cPoints = { lt75: 0, mid: 1, high: 2 }[hba1c];
  const treatmentPoints = TREATMENTS.find((t) => t.key === treatment)?.points || 0;

  const score = (type1 ? 1 : 0) + (longStanding ? 1 : 0) + hypoPoints + hba1cPoints + treatmentPoints;

  let band;
  if (anyAutoFlag) {
    band = { labelKey: 'ramadan_band_very_high_label', color: '#EF4444', textKey: 'ramadan_band_very_high_text' };
  } else if (score <= 3) {
    band = { labelKey: 'ramadan_band_low_label', color: '#10B981', textKey: 'ramadan_band_low_text' };
  } else if (score <= 6) {
    band = { labelKey: 'ramadan_band_moderate_label', color: '#F59E0B', textKey: 'ramadan_band_moderate_text' };
  } else {
    band = { labelKey: 'ramadan_band_high_label', color: '#EF4444', textKey: 'ramadan_band_high_text' };
  }

  return (
    <ToolPageLayout
      title={t('ramadan_title')}
      subtitle={t('ramadan_subtitle')}
      specialty="endocrinologue"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('ramadan_info')}
        </p>
      </div>

      <div className="mb-8">
        <h3 className="font-semibold text-[#0F172A] mb-3">{t('ramadan_very_high_heading')}</h3>
        <div className="space-y-2">
          {AUTO_VERY_HIGH.map((f) => (
            <label key={f.key} className="flex gap-3 items-start bg-white border border-[#E2E8F0] rounded-xl p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!autoFlags[f.key]}
                onChange={(e) => setAutoFlags({ ...autoFlags, [f.key]: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#0596DE]"
              />
              <span className="text-sm text-[#0F172A]">{t(f.labelKey)}</span>
            </label>
          ))}
        </div>
      </div>

      {!anyAutoFlag && (
        <div className="space-y-5 mb-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex gap-3 items-center bg-white border border-[#E2E8F0] rounded-xl p-3 cursor-pointer">
              <input type="checkbox" checked={type1} onChange={(e) => setType1(e.target.checked)} className="w-4 h-4 accent-[#0596DE]" />
              <span className="text-sm text-[#0F172A]">{t('ramadan_type1_label')}</span>
            </label>
            <label className="flex gap-3 items-center bg-white border border-[#E2E8F0] rounded-xl p-3 cursor-pointer">
              <input type="checkbox" checked={longStanding} onChange={(e) => setLongStanding(e.target.checked)} className="w-4 h-4 accent-[#0596DE]" />
              <span className="text-sm text-[#0F172A]">{t('ramadan_longstanding_label')}</span>
            </label>
          </div>

          <div>
            <label className="text-sm font-medium text-[#0F172A] mb-1 block">{t('ramadan_hypo_label')}</label>
            <select value={hypoFreq} onChange={(e) => setHypoFreq(e.target.value)} className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]">
              <option value="aucune">{t('ramadan_hypo_none')}</option>
              <option value="rare">{t('ramadan_hypo_rare')}</option>
              <option value="frequente">{t('ramadan_hypo_frequent')}</option>
              <option value="severe">{t('ramadan_hypo_severe')}</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-[#0F172A] mb-1 block">{t('ramadan_hba1c_label')}</label>
            <select value={hba1c} onChange={(e) => setHba1c(e.target.value)} className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]">
              <option value="lt75">{t('ramadan_hba1c_lt75')}</option>
              <option value="mid">{t('ramadan_hba1c_mid')}</option>
              <option value="high">{t('ramadan_hba1c_high')}</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-[#0F172A] mb-1 block">{t('ramadan_treatment_label')}</label>
            <select value={treatment} onChange={(e) => setTreatment(e.target.value)} className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]">
              {TREATMENTS.map((tr) => (
                <option key={tr.key} value={tr.key}>{t(tr.labelKey)}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={() => setShowResult(true)}
            className="bg-[#0596DE] text-white font-medium text-sm px-5 py-2.5 rounded-xl hover:bg-[#047CBA] transition-colors"
          >
            {t('ramadan_see_result_btn')}
          </button>
        </div>
      )}

      {(anyAutoFlag || showResult) && (
        <div className="rounded-2xl p-6 border" style={{ borderColor: `${band.color}40`, backgroundColor: `${band.color}0D` }}>
          <div className="flex items-center gap-2 mb-2">
            <Moon className="w-5 h-5" style={{ color: band.color }} />
            <span className="font-bold text-lg" style={{ color: band.color }}>{t(band.labelKey)}</span>
          </div>
          <p className="text-sm text-[#0F172A]">{t(band.textKey)}</p>
        </div>
      )}

      <div className="mt-6 bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-2xl p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
        <p className="text-sm text-[#92400E]">
          {t('ramadan_disclaimer')}
        </p>
      </div>
    </ToolPageLayout>
  );
};

export default RamadanDiabetesRisk;
